"use client";
import React, { useState, useEffect, useMemo } from "react";
import { useEditor, EditorContent } from "@tiptap/react";
import { BubbleMenu } from "@tiptap/react/menus";
import { useGlobalStore } from "~/stores/store-provider";
import debounce from "lodash-es/debounce";
import StarterKit from "@tiptap/starter-kit";
import { TableKit } from "@tiptap/extension-table";
import CodeBlockLowlight from "@tiptap/extension-code-block-lowlight";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import TaskList from "@tiptap/extension-task-list";
import TaskItem from "@tiptap/extension-task-item";
import Image from "@tiptap/extension-image";
import Blockquote from "@tiptap/extension-blockquote";
import { UploadedImage } from "./extensions/uploaded-image";
import { AssetImage } from "./extensions/asset-image";
import HorizontalRule from "@tiptap/extension-horizontal-rule";
import { common, createLowlight } from "lowlight";
import css from "highlight.js/lib/languages/css";
import js from "highlight.js/lib/languages/javascript";
import ts from "highlight.js/lib/languages/typescript";
import python from "highlight.js/lib/languages/python";
import html from "highlight.js/lib/languages/xml";
import sql from "highlight.js/lib/languages/sql";
import TiptapToolbar from "./tiptap-toolbar";
import NoDocument from "./no-document";
import AIGeneratingOverlay from "./ai-generating-overlay";
import AIEditPanel from "./ai-edit-panel";
import { getDocumentAIStatus } from "~/lib/network/analysis-document";
import { uploadEditorImage } from "~/lib/network/editor";
import styles from "./tiptap-editor.module.css";
import { LuMaximize, LuMinimize } from "react-icons/lu";
import Toast, { ToastMessage } from "~/components/ui/toast";

// Create a lowlight instance with common languages loaded
const lowlight = createLowlight(common);

// Register additional languages
lowlight.register("html", html);
lowlight.register("css", css);
lowlight.register("js", js);
lowlight.register("ts", ts);
lowlight.register("python", python);
lowlight.register("sql", sql);

interface TiptapEditorProps {
  sessionId: string;
  isEditorExpanded: boolean;
  setIsEditorExpanded: (isEditorExpanded: boolean) => void;
}

const TiptapEditor = ({
  sessionId,
  isEditorExpanded,
  setIsEditorExpanded,
}: TiptapEditorProps) => {
  const [linkMenuUpdateCount, setLinkMenuUpdateCount] = useState(0);
  const [documentLoaded, setDocumentLoaded] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [toast, setToast] = useState<ToastMessage | null>(null);

  const {
    document,
    isLoading,
    isSaving,
    hasUnsavedChanges,
    lastSaved,
    saveError,
    saveDocument,
    fetchDocument,
    setHasUnsavedChanges,
  } = useGlobalStore((state: any) => state);

  // Handle image drop and upload
  const handleImageDrop = async (imageFile: File, pos: number) => {
    // Validate file
    if (imageFile.size > 10 * 1024 * 1024) {
      setToast({ type: "warning", message: "Image should be less than 10 MB" });
      return;
    }

    // Insert loading placeholder immediately
    const tempImageId = `loading-${Date.now()}`;
    if (editor) {
      editor
        .chain()
        .focus()
        .insertContentAt(pos, {
          type: "uploadedImage",
          attrs: {
            imageId: tempImageId,
            sessionId: sessionId,
            alt: imageFile.name,
            isDrop: true,
          },
        })
        .run();
    }

    // Upload and replace with actual image
    try {
      // Upload to S3 via API
      const response = await uploadEditorImage(sessionId, imageFile);

      // Find and replace the loading placeholder
      if (editor) {
        const { state } = editor;
        let placeholderPos = -1;

        state.doc.descendants((node, pos) => {
          if (
            node.type.name === "uploadedImage" &&
            node.attrs.imageId === tempImageId
          ) {
            placeholderPos = pos;
            return false;
          }
        });

        if (placeholderPos !== -1) {
          editor
            .chain()
            .focus()
            .setNodeSelection(placeholderPos)
            .insertContent({
              type: "uploadedImage",
              attrs: {
                imageId: response.image_id,
                sessionId: sessionId,
                alt: response.filename,
                isDrop: false,
              },
            })
            .run();
        }
      }
    } catch (error) {
      setToast({ type: "error", message: "Failed to upload image" });
    }
  };

  // Handle drag and drop file uploads
  const handleDrop = (view: any, event: DragEvent) => {
    const files = Array.from(event.dataTransfer?.files || []);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (!imageFile) {
      return false; // No image file, use default behavior
    }

    event.preventDefault();

    // Get the drop position
    const coordinates = view.posAtCoords({
      left: event.clientX,
      top: event.clientY,
    });
    if (!coordinates) return true;

    const { pos } = coordinates;

    // Handle image drop
    handleImageDrop(imageFile, pos);

    return true; // We handled the drop
  };

  // Handle paste events for image uploads
  const handlePaste = (view: any, event: ClipboardEvent) => {
    const files = Array.from(event.clipboardData?.files || []);
    const imageFile = files.find((file) => file.type.startsWith("image/"));

    if (!imageFile) {
      return false; // No image file, use default behavior
    }

    event.preventDefault();

    // Get the current cursor position
    const { selection } = view.state;
    const pos = selection.from;

    // Handle image paste
    handleImageDrop(imageFile, pos);

    return true; // We handled the paste
  };

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading: {
          levels: [1, 2, 3, 4, 5, 6],
        },
        codeBlock: false, // Disable default code block to use CodeBlockLowlight
        blockquote: false, // Disable default blockquote to use the extension
        horizontalRule: false, // Disable default horizontal rule to use the extension
      }),
      TableKit,
      CodeBlockLowlight.configure({
        lowlight,
      }),
      TextAlign.configure({
        types: ["heading", "paragraph"],
      }),
      Link.configure({
        openOnClick: false,
        linkOnPaste: true,
        HTMLAttributes: {
          class: "text-blue-500 underline hover:text-blue-700",
          rel: "noopener noreferrer",
          target: null, // Prevent default target behavior
        },
      }),
      Underline,
      TaskList,
      TaskItem.configure({
        nested: true,
      }),
      Image.configure({
        inline: true,
        allowBase64: true,
      }),
      UploadedImage,
      AssetImage,
      Blockquote,
      HorizontalRule,
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "m-5 focus:outline-none",
      },
      handleDrop,
      handlePaste,
    },
    immediatelyRender: false,
  });

  // Fetch document on mount
  useEffect(() => {
    if (sessionId) {
      fetchDocument(sessionId);
    }
  }, [sessionId, fetchDocument]);

  // Load document content into editor
  useEffect(() => {
    if (editor && document && !documentLoaded) {
      if (document.content_html) {
        editor.commands.setContent(document.content_html);
      }
      setDocumentLoaded(true);
    }
  }, [editor, document, documentLoaded]);

  // Create debounced save function
  const debouncedSave = useMemo(
    () =>
      debounce(async () => {
        if (!editor || !document) return;

        try {
          // Get current HTML content
          const html = editor.getHTML();

          // Save HTML content
          await saveDocument(sessionId, html);
        } catch (error) {
          console.error("Auto-save failed:", error);
        }
      }, 2000),
    [editor, document, sessionId, saveDocument]
  );

  // Update link menu when selection changes
  useEffect(() => {
    if (!editor) return;

    const updateLinkMenu = () => {
      setLinkMenuUpdateCount((prev) => prev + 1);
    };

    editor.on("selectionUpdate", updateLinkMenu);
    editor.on("transaction", updateLinkMenu);

    return () => {
      editor.off("selectionUpdate", updateLinkMenu);
      editor.off("transaction", updateLinkMenu);
    };
  }, [editor]);

  // Setup autosave on content changes
  useEffect(() => {
    if (!editor) return;

    const handleUpdate = () => {
      // Only trigger save if document is loaded to avoid saving during initial load
      if (documentLoaded && document) {
        setHasUnsavedChanges(true);
        debouncedSave();
      }
    };

    editor.on("update", handleUpdate);

    return () => {
      editor.off("update", handleUpdate);
    };
  }, [editor, documentLoaded, document, setHasUnsavedChanges, debouncedSave]);

  // Cleanup debounced save on unmount
  useEffect(() => {
    return () => {
      debouncedSave.cancel();
    };
  }, [debouncedSave]);

  // Document generation polling
  useEffect(() => {
    // Only start polling if document is busy and not already polling
    if (document?.ai_status === "busy" && !isPolling) {
      console.log("Starting document polling for session:", sessionId);
      setIsPolling(true);

      const startTime = Date.now();
      const TIMEOUT_MS = 5 * 60 * 1000; // 5 minutes

      const interval = setInterval(async () => {
        try {
          // Check if we've timed out
          if (Date.now() - startTime > TIMEOUT_MS) {
            clearInterval(interval);
            setIsPolling(false);
            console.log("Document generation timed out");
            return;
          }

          // Poll status
          const statusResponse = await getDocumentAIStatus(sessionId);

          if (statusResponse.ai_status === "idle") {
            clearInterval(interval);
            setIsPolling(false);
            console.log("Document generation completed, fetching document");

            // Fetch the complete document and update editor
            const updatedDocument = await fetchDocument(sessionId);

            // Small delay to ensure state update is processed, then update editor
            if (editor && updatedDocument?.content_html) {
              console.log(
                "Updating editor with AI-generated content",
                updatedDocument.content_html
              );
              editor.commands.setContent(updatedDocument.content_html);
            }
          }
        } catch (error) {
          console.error("Polling error:", error);
          // Continue polling on error - don't stop
        }
      }, 3000);

      // Cleanup function - runs on unmount or dependency change
      return () => {
        clearInterval(interval);
        setIsPolling(false);
        console.log("Polling cleanup");
      };
    }

    // Reset polling flag if document is no longer busy
    if (document?.ai_status !== "busy" && isPolling) {
      setIsPolling(false);
    }
  }, [document?.ai_status, sessionId, fetchDocument]);

  // Reset documentLoaded flag when AI generation starts to allow new content loading
  useEffect(() => {
    if (document?.ai_status === "busy") {
      setDocumentLoaded(false);
    }
  }, [document?.ai_status]);

  // Function to get current link URL dynamically
  const getCurrentLinkUrl = () => {
    if (!editor) return "";
    if (!editor.isActive("link")) return "";
    const attrs = editor.getAttributes("link");
    return attrs?.href || "";
  };

  // Show loading state
  if (isLoading) {
    return (
      <div className="h-full w-full flex items-center justify-center">
        <div className="text-center">
          <div className="loading loading-spinner loading-lg"></div>
          <p className="mt-4 text-gray-600">Loading document...</p>
        </div>
      </div>
    );
  }

  // Show AI generation overlay
  if (document?.ai_status === "busy") {
    return <AIGeneratingOverlay />;
  }

  // Show empty state if no document
  if (!document && !isLoading) {
    return <NoDocument sessionId={sessionId} />;
  }

  return (
    <div className="h-full w-full flex flex-col">
      {/* Toast component */}
      <Toast toast={toast} />

      {/* Toolbar */}
      <TiptapToolbar
        editor={editor}
        sessionId={sessionId}
        isSaving={isSaving}
        hasUnsavedChanges={hasUnsavedChanges}
        lastSaved={lastSaved}
        saveError={saveError}
      />

      {/* AI Edit Panel - now floating */}
      <AIEditPanel sessionId={sessionId} />

      <div className="absolute ml-6 top-40 z-50">
        <button
          className="btn btn-sm btn-dash btn-circle text-gray-500 hover:text-white"
          onClick={() => setIsEditorExpanded(!isEditorExpanded)}
        >
          {isEditorExpanded ? (
            <LuMinimize size={14} />
          ) : (
            <LuMaximize size={14} />
          )}
        </button>
      </div>

      {/* Editor container */}
      <div className="flex-1 overflow-y-auto bg-gray-100 p-6">
        <div className="max-w-6xl mx-auto bg-white rounded-lg shadow-lg min-h-[calc(100vh-200px)]">
          <div
            className={`${styles.editorWrapper} prose prose-gray max-w-none p-16`}
          >
            <EditorContent editor={editor} />

            {/* Bubble Menu - React Component */}
            {editor && (
              <BubbleMenu
                editor={editor}
                shouldShow={({ editor, view, state }) => {
                  const { selection } = state;
                  const { $from, empty } = selection;

                  // Don't show on any images
                  if (
                    editor.isActive("uploadedImage") ||
                    editor.isActive("assetImage") ||
                    editor.isActive("image")
                  ) {
                    return false;
                  }

                  // Only show when there's an actual text selection (not empty)
                  return !empty;
                }}
              >
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "4px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    display: "flex",
                    gap: "2px",
                  }}
                >
                  <button
                    onClick={() => editor?.chain().focus().toggleBold().run()}
                    style={{
                      background: editor?.isActive("bold")
                        ? "#ddd"
                        : "transparent",
                      border: "none",
                      padding: "4px 8px",
                      borderRadius: "2px",
                      cursor: "pointer",
                      fontWeight: "bold",
                    }}
                  >
                    B
                  </button>
                  <button
                    onClick={() => editor?.chain().focus().toggleItalic().run()}
                    style={{
                      background: editor?.isActive("italic")
                        ? "#ddd"
                        : "transparent",
                      border: "none",
                      padding: "4px 8px",
                      borderRadius: "2px",
                      cursor: "pointer",
                      fontStyle: "italic",
                    }}
                  >
                    I
                  </button>
                  <button
                    onClick={() =>
                      editor?.chain().focus().toggleUnderline().run()
                    }
                    style={{
                      background: editor?.isActive("underline")
                        ? "#ddd"
                        : "transparent",
                      border: "none",
                      padding: "4px 8px",
                      borderRadius: "2px",
                      cursor: "pointer",
                      textDecoration: "underline",
                    }}
                  >
                    U
                  </button>
                </div>
              </BubbleMenu>
            )}

            {/* Link BubbleMenu - Shows when cursor is on a link */}
            {editor && (
              <BubbleMenu
                editor={editor}
                shouldShow={({ editor }) => {
                  // Only show when cursor is on a link
                  return editor.isActive("link");
                }}
                updateDelay={0}
                pluginKey="linkBubbleMenu"
                className={styles.linkBubbleMenu}
              >
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "6px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                    maxWidth: "300px",
                  }}
                >
                  <div
                    style={{
                      flex: 1,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      color: "#666",
                      fontSize: "12px",
                    }}
                  >
                    {getCurrentLinkUrl()}
                  </div>

                  <div style={{ display: "flex", gap: "4px" }}>
                    <button
                      onClick={() => {
                        const href = getCurrentLinkUrl();
                        if (href) {
                          window.open(href, "_blank", "noopener,noreferrer");
                        }
                      }}
                      title="Open link"
                      style={{
                        background: "transparent",
                        border: "1px solid #ddd",
                        padding: "4px 8px",
                        borderRadius: "3px",
                        cursor: "pointer",
                        fontSize: "11px",
                        color: "#0066cc",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f0f8ff")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      Open
                    </button>

                    <button
                      onClick={() => {
                        const previousUrl = getCurrentLinkUrl();
                        const url = prompt("Enter URL:", previousUrl);

                        if (url === null) return;

                        if (url === "") {
                          editor
                            .chain()
                            .focus()
                            .extendMarkRange("link")
                            .unsetLink()
                            .run();
                        } else {
                          editor
                            .chain()
                            .focus()
                            .extendMarkRange("link")
                            .setLink({ href: url })
                            .run();
                        }
                      }}
                      title="Edit link"
                      style={{
                        background: "transparent",
                        border: "1px solid #ddd",
                        padding: "4px 8px",
                        borderRadius: "3px",
                        cursor: "pointer",
                        fontSize: "11px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f0f0f0")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      Edit
                    </button>

                    <button
                      onClick={() => {
                        editor
                          .chain()
                          .focus()
                          .extendMarkRange("link")
                          .unsetLink()
                          .run();
                      }}
                      title="Remove link"
                      style={{
                        background: "transparent",
                        border: "1px solid #ddd",
                        padding: "4px 8px",
                        borderRadius: "3px",
                        cursor: "pointer",
                        fontSize: "11px",
                        color: "#dc2626",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#fee2e2")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      Remove
                    </button>
                  </div>
                </div>
              </BubbleMenu>
            )}

            {/* Table BubbleMenu - Shows when cursor is in a table */}
            {editor && (
              <BubbleMenu
                editor={editor}
                shouldShow={({ editor }) => {
                  // Only show when cursor is inside a table
                  return editor.isActive("table");
                }}
                updateDelay={0}
                className={styles.tableMenuTop}
              >
                <div
                  style={{
                    background: "#fff",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    padding: "4px",
                    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                    display: "flex",
                    gap: "4px",
                    fontSize: "12px",
                  }}
                >
                  {/* Row controls */}
                  <div
                    style={{
                      display: "flex",
                      gap: "2px",
                      borderRight: "1px solid #e0e0e0",
                      paddingRight: "4px",
                    }}
                  >
                    <button
                      onClick={() =>
                        editor.chain().focus().addRowBefore().run()
                      }
                      title="Add row above"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "4px 6px",
                        borderRadius: "2px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f0f0f0")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      ↑ Row
                    </button>
                    <button
                      onClick={() => editor.chain().focus().addRowAfter().run()}
                      title="Add row below"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "4px 6px",
                        borderRadius: "2px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f0f0f0")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      ↓ Row
                    </button>
                    <button
                      onClick={() => editor.chain().focus().deleteRow().run()}
                      title="Delete row"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "4px 6px",
                        borderRadius: "2px",
                        cursor: "pointer",
                        color: "#dc2626",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#fee2e2")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      ✕ Row
                    </button>
                  </div>

                  {/* Column controls */}
                  <div
                    style={{
                      display: "flex",
                      gap: "2px",
                      borderRight: "1px solid #e0e0e0",
                      paddingRight: "4px",
                    }}
                  >
                    <button
                      onClick={() =>
                        editor.chain().focus().addColumnBefore().run()
                      }
                      title="Add column left"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "4px 6px",
                        borderRadius: "2px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f0f0f0")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      ← Col
                    </button>
                    <button
                      onClick={() =>
                        editor.chain().focus().addColumnAfter().run()
                      }
                      title="Add column right"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "4px 6px",
                        borderRadius: "2px",
                        cursor: "pointer",
                        display: "flex",
                        alignItems: "center",
                        gap: "2px",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#f0f0f0")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      → Col
                    </button>
                    <button
                      onClick={() =>
                        editor.chain().focus().deleteColumn().run()
                      }
                      title="Delete column"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "4px 6px",
                        borderRadius: "2px",
                        cursor: "pointer",
                        color: "#dc2626",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#fee2e2")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      ✕ Col
                    </button>
                  </div>

                  {/* Table controls */}
                  <div style={{ display: "flex", gap: "2px" }}>
                    <button
                      onClick={() =>
                        editor.chain().focus().toggleHeaderRow().run()
                      }
                      title="Toggle header row"
                      style={{
                        background:
                          editor.can().toggleHeaderRow() &&
                          editor.isActive("tableHeader")
                            ? "#e0e0e0"
                            : "transparent",
                        border: "none",
                        padding: "4px 6px",
                        borderRadius: "2px",
                        cursor: "pointer",
                      }}
                      onMouseEnter={(e) => {
                        if (!editor.isActive("tableHeader"))
                          e.currentTarget.style.background = "#f0f0f0";
                      }}
                      onMouseLeave={(e) => {
                        if (!editor.isActive("tableHeader"))
                          e.currentTarget.style.background = "transparent";
                      }}
                    >
                      Header
                    </button>
                    <button
                      onClick={() => editor.chain().focus().deleteTable().run()}
                      title="Delete table"
                      style={{
                        background: "transparent",
                        border: "none",
                        padding: "4px 6px",
                        borderRadius: "2px",
                        cursor: "pointer",
                        color: "#dc2626",
                      }}
                      onMouseEnter={(e) =>
                        (e.currentTarget.style.background = "#fee2e2")
                      }
                      onMouseLeave={(e) =>
                        (e.currentTarget.style.background = "transparent")
                      }
                    >
                      Delete Table
                    </button>
                  </div>
                </div>
              </BubbleMenu>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TiptapEditor;
